import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";

// Paired with collection_generator_products_controller.js by direct controller-to-controller
// calls in both directions: this one calls its `loadCollection(collectionId)` to start a
// generate or regenerate, and it calls back into `onProductsPageLoaded`/`onProductsLoadFailed`/
// `onCollectionSkipped` with the outcome.
//
// `.generated-products-grid` is a descendant of this mount rather than the same node, so each side
// looks up the element that actually carries the other's `data-controller` - a querySelector down
// from here, a closest up from there.

export default class extends Controller {
  static get targets() {
    return [
      "mobileActions", "title", "horizontal", "productsGrid", "artContainer", "promptArea",
      "startHere", "continueHere", "generatedDivider", "chooseLegendBefore", "chooseLegendAfter",
      "totalProducts", "primaryButton", "ctaGenerate", "ctaSend", "ctaRegenerate",
      "internationalSelect", "budgetSelect", "tooltipWrapper"
    ];
  }

  static get values() {
    return { collections: Object, selectCollectionUrl: String };
  }

  static get classes() {
    return ["generated", "hidden", "btnGhost", "btnObsidian"];
  }

  connect() {
    // select2/tooltip are jQuery plugins, so these stay jQuery-wrapped for the plugin calls
    // below even though the lookup itself now goes through a Target instead of `.find()`.
    this.$internationalSelect = $(this.internationalSelectTarget);
    this.$budgetSelect = $(this.budgetSelectTarget);
    this.$tooltipWrapper = $(this.tooltipWrapperTarget);

    this.localBudgetOptions = this.collectionsValue.domestic;
    this.internationalBudgetOptions = this.collectionsValue.international;

    this.areProductsLoaded = false;
    this.canGenerate = false;
    this.isGenerating = false;
    this.generatedInternational = null;
    this.generatedBudget = null;
    this.internationalSelected = null;

    // Remove any lingering Select2 containers / generated-state class from a bfcache restore.
    document.querySelectorAll(`.${this.generatedClass}`).forEach((el) => el.classList.remove(this.generatedClass));
    document.querySelectorAll(".select2-container").forEach((el) => el.remove());

    if (this.$internationalSelect.hasClass("select2-hidden-accessible")) {
      this.$internationalSelect.select2("destroy");
    }
    if (this.$budgetSelect.hasClass("select2-hidden-accessible")) {
      this.$budgetSelect.select2("destroy");
    }

    this.$tooltipWrapper.tooltip();

    this.localBudgetOptions.unshift({ id: "", text: "" });
    this.internationalBudgetOptions.unshift({ id: "", text: "" });

    this.$internationalSelect.select2({
      minimumResultsForSearch: Infinity,
      placeholder: "Select Location"
    });

    this.$budgetSelect.select2({
      minimumResultsForSearch: Infinity,
      placeholder: "Select Budget",
      data: this.localBudgetOptions
    });

    // Both handlers stay paired with the select2 setup above rather than moving to
    // data-action: select2's own close event is announced through jQuery only, and the budget
    // select's change handler has to see select2's programmatic value changes in
    // updateBudgetOptions() below, not just a person's own interaction.
    this.onInternationalClose = this.onInternationalClose.bind(this);
    this.onBudgetChange = this.onBudgetChange.bind(this);

    this.$internationalSelect.on("select2:close", this.onInternationalClose);
    this.$budgetSelect.on("change", this.onBudgetChange);
  }

  disconnect() {
    this.$internationalSelect.off("select2:close", this.onInternationalClose);
    this.$budgetSelect.off("change", this.onBudgetChange);
  }

  onInternationalClose() {
    this.internationalSelected = this.$internationalSelect.val();
    this.updateBudgetOptions();
    this.checkCanGenerate();
  }

  onBudgetChange() {
    this.checkCanGenerate();
  }

  updateBudgetOptions() {
    const data = this.internationalSelected === "true" ?
      this.internationalBudgetOptions : this.localBudgetOptions;

    this.$budgetSelect.select2("destroy");
    this.$budgetSelect.empty();

    this.$budgetSelect.select2({
      minimumResultsForSearch: Infinity,
      data: data,
      placeholder: "Select Budget"
    });

    this.$budgetSelect.val(null).trigger("change");
    this.$budgetSelect.prop("disabled", false);
  }

  checkCanGenerate() {
    const hasLocation = this.$internationalSelect.val() && this.$internationalSelect.val() !== "";
    const hasBudget = this.$budgetSelect.val() && this.$budgetSelect.val() !== "";
    this.canGenerate = hasLocation && hasBudget;

    if (!this.canGenerate) {
      this.$tooltipWrapper.tooltip();
    } else {
      this.$tooltipWrapper.tooltip("destroy");
    }

    this.updatePrimaryButtonState();
  }

  handlePrimaryCTA() {
    if (!this.areProductsLoaded || this.hasParametersChanged()) {
      this.generateCollection();
    } else {
      this.nextStep();
    }
  }

  generateCollection() {
    const budgetId = this.$budgetSelect.val();
    const international = this.$internationalSelect.val();

    this.isGenerating = true;
    this.generatedInternational = international;
    this.generatedBudget = budgetId;
    this.updatePrimaryButtonState();

    const products = this.productsController();
    if (products) { products.loadCollection(budgetId); }
  }

  nextStep() {
    const id = this.$budgetSelect.val();

    const body = new URLSearchParams();
    body.append("id", id);

    const options = RequestHelper.defaultPostOptions();
    options.body = body;

    fetch(this.selectCollectionUrlValue, options)
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          if (data.redirect_url) { Turbo.visit(data.redirect_url); }
          return;
        }

        window.ltAlert({ message: data.error || "Failed to proceed to next step.", type: "toast-error", duration: 2500 });
      })
      .catch(() => {
        window.ltAlert({ message: "Failed to proceed to next step.", type: "toast-error", duration: 2500 });
      });
  }

  hasParametersChanged() {
    if (!this.areProductsLoaded) {
      return false;
    }

    const currentInternational = this.$internationalSelect.val();
    const currentBudget = this.$budgetSelect.val();

    return currentInternational !== this.generatedInternational ||
      currentBudget !== this.generatedBudget;
  }

  // `.generated-products-grid` carries `data-controller="collection-generator-products"` and is a
  // descendant of this mount, not the same node.
  productsController() {
    return this.application.getControllerForElementAndIdentifier(this.productsGridTarget, "collection-generator-products");
  }

  // Called by collection-generator-products when the requested collectionId is already current.
  onCollectionSkipped() {
    this.isGenerating = false;
    this.updatePrimaryButtonState();
  }

  onProductsPageLoaded({ totalProducts, isFirstPage }) {
    if (isFirstPage) { this.areProductsLoaded = true; }
    this.setTotalProducts(totalProducts);
    this.isGenerating = false;
    this.render();
  }

  onProductsLoadFailed() {
    this.areProductsLoaded = false;
    this.isGenerating = false;
    this.render();
  }

  setTotalProducts(value) {
    this.totalProductsTargets.forEach((el) => { el.textContent = value; });
  }

  render() {
    this.horizontalTarget.classList.toggle(this.generatedClass, this.areProductsLoaded);
    this.promptAreaTarget.classList.toggle(this.generatedClass, this.areProductsLoaded);

    this.mobileActionsTarget.classList.toggle(this.hiddenClass, !this.areProductsLoaded);
    this.titleTarget.classList.toggle(this.hiddenClass, this.areProductsLoaded);
    this.productsGridTarget.classList.toggle(this.hiddenClass, !this.areProductsLoaded);
    this.artContainerTarget.classList.toggle(this.hiddenClass, this.areProductsLoaded);
    this.startHereTarget.classList.toggle(this.hiddenClass, this.areProductsLoaded);
    this.continueHereTarget.classList.toggle(this.hiddenClass, !this.areProductsLoaded);
    this.generatedDividerTarget.classList.toggle(this.hiddenClass, !this.areProductsLoaded);
    this.chooseLegendBeforeTarget.classList.toggle(this.hiddenClass, this.areProductsLoaded);
    this.chooseLegendAfterTarget.classList.toggle(this.hiddenClass, !this.areProductsLoaded);

    this.updatePrimaryButtonState();
  }

  updatePrimaryButtonState() {
    const changed = this.areProductsLoaded && this.hasParametersChanged();

    this.primaryButtonTarget.classList.toggle(this.btnGhostClass, changed);
    this.primaryButtonTarget.classList.toggle(this.btnObsidianClass, !changed);
    this.primaryButtonTarget.disabled = !this.canGenerate || this.isGenerating;

    this.ctaGenerateTarget.classList.toggle(this.hiddenClass, this.areProductsLoaded);
    this.ctaSendTarget.classList.toggle(this.hiddenClass, !(this.areProductsLoaded && !this.hasParametersChanged()));
    this.ctaRegenerateTarget.classList.toggle(this.hiddenClass, !(this.areProductsLoaded && this.hasParametersChanged()));
  }
}
